import { Fragment, cloneElement, isValidElement, useMemo, useState, useRef, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { buildGlossaryRegex, glossaryDefinition } from "../translations/glossary";

const SKIP_TAGS = new Set(["h1", "h2", "h3", "a", "button", "input", "textarea", "label", "select", "option"]);
const SKIP_COMPONENT = /Trigger|Button|Link|Toggle|Tab|Item$/;
const MAX_PER_TERM = 2;

/** Mot du glossaire : souligné en pointillé ambre, définition au survol ou au clic. */
export const GlossTerm = ({ termKey, children }) => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const ref = useRef(null);
  const def = glossaryDefinition(termKey, language);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setFlip(rect.left > window.innerWidth - 300);
    setOpen(true);
  };

  if (!def) return children;

  return (
    <span className="relative inline" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          open ? setOpen(false) : show();
        }}
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        className="gloss-term"
        aria-expanded={open}
        data-testid={`gloss-term-${termKey}`}
      >
        {children}
      </button>
      {open && (
        <span
          role="tooltip"
          className={`gloss-pop ${flip ? "gloss-pop-right" : ""}`}
          data-testid={`gloss-pop-${termKey}`}
        >
          {def}
        </span>
      )}
    </span>
  );
};

const glossify = (text, ctx) => {
  if (!text || text.length < 3 || !/\p{L}/u.test(text)) return text;
  const { regex, lookup, counts } = ctx;
  regex.lastIndex = 0;
  const out = [];
  let last = 0;
  let match;
  let touched = false;
  while ((match = regex.exec(text)) !== null) {
    const raw = match[1];
    const key = lookup[raw.toLowerCase()];
    if (!key) continue;
    if ((counts[key] || 0) >= MAX_PER_TERM) continue;
    counts[key] = (counts[key] || 0) + 1;
    touched = true;
    if (match.index > last) out.push(text.slice(last, match.index));
    out.push(
      <GlossTerm key={`${key}-${match.index}`} termKey={key}>
        {raw}
      </GlossTerm>
    );
    last = match.index + raw.length;
  }
  if (!touched) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const walk = (node, ctx) => {
  if (typeof node === "string") return glossify(node, ctx);
  if (Array.isArray(node)) return node.map((child, i) => <Fragment key={i}>{walk(child, ctx)}</Fragment>);
  if (!isValidElement(node)) return node;
  if (typeof node.type === "string" && SKIP_TAGS.has(node.type)) return node;
  if (typeof node.type !== "string") {
    const name = node.type?.displayName || node.type?.name || "";
    if (SKIP_COMPONENT.test(name)) return node;
  }
  if (node.props?.onClick || node.props?.["aria-expanded"] !== undefined) return node;
  if (node.props?.["data-no-gloss"] !== undefined) return node;
  const children = node.props?.children;
  if (children === undefined || children === null || typeof children === "function") return node;
  return cloneElement(node, undefined, walk(children, ctx));
};

/** Applique le glossaire à un arbre JSX déjà construit. */
export const glossifyTree = (tree, language) => walk(tree, { ...buildGlossaryRegex(language), counts: {} });

/** Enveloppe un bloc de JSX : les mots du glossaire y deviennent cliquables. */
export const Glossary = (props) => {
  const { language } = useLanguage();
  const built = useMemo(() => buildGlossaryRegex(language), [language]);
  const ctx = { ...built, counts: {} };
  return <>{walk(props.children, ctx)}</>;
};

export default Glossary;
